<script lang="ts" setup>
import { computed } from "vue";
import OmniTextarea from "@/components/omni/OmniTextarea.vue";
import AttachFileAction from "@/views/chats/components/actions/AttachFileAction.vue";
import MessageTemplatesAction from "@/views/chats/components/actions/MessageTemplatesAction.vue";
import { IMAGE_MIME_TYPES, PDF_MIME_TYPE } from "@/constants/chat";
import type { ChatTemplate } from "@/api/chat/dto/chat-template";

const props = withDefaults(
  defineProps<{
    file: File | null;
    disabled?: boolean;
    loading?: boolean;
  }>(),
  {
    disabled: false,
    loading: false,
  },
);

const emit = defineEmits<{
  (e: "update:file", value: File | null): void;
  (e: "change"): void;
  (e: "submit"): void;
}>();

defineOptions({ name: "NewMessageTextarea" });

const text = defineModel<string>({ default: "" });

const fileUrl = computed<string>(() => {
  return props.file ? URL.createObjectURL(props.file) : "";
});

const isImageFile = computed<boolean>(() => {
  return !!props.file && IMAGE_MIME_TYPES.includes(props.file.type);
});

const isPdfFile = computed<boolean>(() => {
  return props.file?.type === PDF_MIME_TYPE;
});

const isSendDisabled = computed<boolean>(() => {
  return props.disabled || (!text.value?.trim() && !props.file);
});

function applyTemplate(template: ChatTemplate) {
  text.value = text.value?.trim() ? `${text.value}\n${template.text}` : template.text;
  emit("change");
}

function removeFile() {
  emit("update:file", null);
}

function submit() {
  if (isSendDisabled.value) {
    return;
  }

  emit("submit");
}

function onPaste(event: ClipboardEvent) {
  const pasted = event.clipboardData?.files?.[0];

  if (pasted) {
    emit("update:file", pasted);
  }
}
</script>

<template>
  <div class="message-textarea">
    <omni-textarea
      v-model="text"
      :custom-new-line="true"
      :disabled="disabled"
      :loading="loading"
      :readonly="loading"
      class="message-textarea__textarea"
      hide-close
      max-rows="10"
      placeholder="Enter Message..."
      rows="1.5"
      @change="emit('change')"
      @paste="onPaste"
      @submit="submit"
    />

    <div class="d-flex justify-space-between align-center">
      <div class="d-flex align-center ga-4 mt-1">
        <message-templates-action :disabled="disabled" @select="applyTemplate" />
        <attach-file-action :disabled="disabled" :file="file" @update:file="emit('update:file', $event)" />
      </div>

      <v-icon :disabled="isSendDisabled" color="primary" @click="submit">mdi-send-outline</v-icon>
    </div>

    <div v-if="file" class="file-attachment mt-2">
      <div class="file-attachment__icon">
        <v-img v-if="isImageFile" :cover="true" :src="fileUrl" class="rounded" max-height="40" max-width="50" />
        <div v-else-if="isPdfFile" class="file-pdf">
          <div class="file-caption">
            <div>
              <v-icon color="#bdbdbd">mdi-attachment</v-icon>
            </div>
            <div>PDF</div>
          </div>
        </div>
      </div>

      {{ file.name }}
      <v-icon class="close-button" size="18" @click="removeFile">mdi-close</v-icon>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.message-textarea {
  background-color: $white;
  padding: 16px;
  border: 1px solid $grey-lighten-2;
  border-radius: 12px;

  &__textarea {
    :deep(.v-field__input) {
      padding: 0;
    }

    :deep(.v-field__outline) {
      display: none;
    }
  }
}

.file-attachment {
  display: flex;
  align-items: center;

  .file-pdf {
    width: 40px;
    height: 40px;
    border: 1px solid $grey-light;
    border-radius: 4px;
    position: relative;

    .file-caption {
      position: absolute;
      text-align: center;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 14px;
      font-weight: bold;
      color: $grey-light;
    }
  }
}
</style>
